import { Injectable } from '@angular/core';
import { type IClaims, Platforms, StorageKeys } from '@core-interfaces/global';
import { IAuthService } from '@core-interfaces/services/auth';
import type { ICreateUserInput } from '@core-ports/inputs/user';
import type { IUser } from '@core-ports/outputs/user';
import { jwtDecode } from 'jwt-decode';
import { firstValueFrom, map, Observable, timeout } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { IMutationCreateUserOutput, IMutationGenerateVerificationCodeOutput, IMutationRefreshTokenOutput, IMutationValidateVerificationCodeOutput, mutation_create_user, mutation_generate_verification_code, mutation_refresh_token, mutation_validate_verification_code } from '@infra-adapters/graphql/auth';
import { IValidateVerificationCodeOutput } from '@core-ports/outputs/auth';
import { Preferences } from '@capacitor/preferences';
import { AnalyticsService } from './analytics.service';
import { AppEvents } from '@core-interfaces/global/events';
import { Platform } from '@ionic/angular/standalone';

// Se renueva un poco antes de vencer: un token que vence en tránsito, o con el
// reloj del teléfono atrasado, llega vencido al servidor.
const ACCESS_TOKEN_RENEW_MARGIN_MS = 30_000;
// Todas las peticiones con el token por vencer esperan la misma renovación: si
// esa se colgara sin límite, se colgaría la app entera.
const REFRESH_TIMEOUT_MS = 10_000;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements IAuthService {
  private refreshInFlight: Promise<IValidateVerificationCodeOutput> | null = null;

  constructor(
    private apollo: Apollo,
    private analyticsService: AnalyticsService,
    private platform: Platform,
  ) { }

  async isAuth(): Promise<boolean> {
    const sessionClaims = await this.getSessionClaimsOrNull();
    return sessionClaims !== null;
  }

  async userSessionData(): Promise<IClaims | null> {
    return this.getSessionClaimsOrNull();
  }

  async setSession(token: string): Promise<void> {
    await Preferences.set({ key: StorageKeys.session_token, value: token });
  }

  async setRefreshToken(refresh_token: string): Promise<void> {
    await Preferences.set({ key: StorageKeys.refresh_token, value: refresh_token });
  }

  async getRefreshToken(): Promise<string | null> {
    const { value } = await Preferences.get({ key: StorageKeys.refresh_token });
    return value;
  }

  async clearSession(): Promise<void> {
    await Preferences.remove({ key: StorageKeys.session_token });
    await Preferences.remove({ key: StorageKeys.refresh_token });
  }

  private async getSessionClaimsOrNull(): Promise<IClaims | null> {
    const { value: sessionToken } = await Preferences.get({ key: StorageKeys.session_token });
    if (!sessionToken) {
      return null;
    }

    try {
      const decodedToken = jwtDecode<IClaims>(sessionToken);
      if (!this.isTokenExpired(decodedToken)) {
        return decodedToken;
      }

      // Access vencido: intentar refresh (ventana deslizante de inactividad BEL-537).
      try {
        await this.refreshTokenOnce();
      } catch {
        await this.clearSession();
        return null;
      }

      const { value: renewed } = await Preferences.get({ key: StorageKeys.session_token });
      if (!renewed) {
        return null;
      }
      const renewedClaims = jwtDecode<IClaims>(renewed);
      if (this.isTokenExpired(renewedClaims)) {
        await this.clearSession();
        return null;
      }
      return renewedClaims;
    } catch (error) {
      await this.clearSession();
      return null;
    }
  }

  private isTokenExpired(claims: IClaims): boolean {
    if (!claims.exp) {
      return false;
    }

    return claims.exp * 1000 <= Date.now();
  }

  registerUser(input: ICreateUserInput): Observable<IUser> {
    return this.apollo.mutate<IMutationCreateUserOutput>({
      mutation: mutation_create_user,
      fetchPolicy: 'network-only',
      variables: {
        input,
      }
    })
      .pipe(
        map((response) => {
          if (response.data && response.data.createUser) {
            return response.data.createUser;
          } else {
            throw new Error('User registration failed');
          }
        })
      )
  }

  async generateVerificationCode(email: string, os?: Platforms): Promise<boolean> {
    await this.analyticsService.logEvent(AppEvents.validation_code_requested);

    const result = await firstValueFrom(
      this.apollo.mutate<IMutationGenerateVerificationCodeOutput>({
        mutation: mutation_generate_verification_code,
        variables: { email, os },
        fetchPolicy: 'network-only',
      })
    );

    if (!result?.data?.generateCode) {
      throw new Error('Verification code generation failed');
    }

    await this.analyticsService.logEvent(AppEvents.validation_code_sent);

    return true;
  }

  validateVerificationCode(email: string, code: string): Observable<IValidateVerificationCodeOutput> {
    return this.apollo.mutate<IMutationValidateVerificationCodeOutput>({
      mutation: mutation_validate_verification_code,
      variables: { email, otp_code: code },
      fetchPolicy: 'network-only',
    }).pipe(
      map((response) => {
        if (response.data && response.data.validateCode) {
          this.analyticsService.logEvent(AppEvents.validation_code_success)
          return response.data.validateCode;
        } else {
          throw new Error('Verification code generation failed');
        }
      })
    );
  }

  async refreshToken(): Promise<IValidateVerificationCodeOutput> {
    const refresh_token = await this.getRefreshToken();
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }

    const result = await firstValueFrom(
      this.apollo.mutate<IMutationRefreshTokenOutput>({
        mutation: mutation_refresh_token,
        variables: { refresh_token },
        fetchPolicy: 'network-only',
      }).pipe(timeout(REFRESH_TIMEOUT_MS))
    );

    if (!result?.data?.refreshToken) {
      throw new Error('Token refresh failed');
    }

    const tokens = result.data.refreshToken;
    await this.setSession(tokens.token);
    if (tokens.refresh_token) {
      await this.setRefreshToken(tokens.refresh_token);
    }

    return tokens;
  }

  // Varias peticiones pueden encontrar el token vencido a la vez: todas esperan
  // la misma renovación en vez de pedir una cada una.
  refreshTokenOnce(): Promise<IValidateVerificationCodeOutput> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshToken().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  // Token para adjuntar a una petición. Si no se puede renovar se devuelve el
  // guardado: el servidor lo rechaza y el error link decide si cerrar la sesión.
  async getValidSessionToken(): Promise<string | null> {
    const { value: token } = await Preferences.get({ key: StorageKeys.session_token });
    if (!token || !this.expiresWithin(token, ACCESS_TOKEN_RENEW_MARGIN_MS)) {
      return token;
    }
    try {
      return (await this.refreshTokenOnce()).token;
    } catch {
      return token;
    }
  }

  private expiresWithin(token: string, marginMs: number): boolean {
    try {
      const { exp } = jwtDecode<IClaims>(token);
      return typeof exp === 'number' && exp * 1000 - Date.now() < marginMs;
    } catch {
      return false;
    }
  }

  /** Renueva la sesión si hay refresh token (uso de la app = reinicia contador 24 h). */
  async renewSessionIfPossible(): Promise<boolean> {
    const refresh_token = await this.getRefreshToken();
    if (!refresh_token) {
      return false;
    }

    const { value: sessionToken } = await Preferences.get({ key: StorageKeys.session_token });
    if (sessionToken) {
      try {
        const claims = jwtDecode<IClaims>(sessionToken);
        // Si el access aún tiene más de la mitad de vida, no hace falta renovar.
        if (claims.exp && claims.exp * 1000 > Date.now() + 12 * 60 * 60 * 1000) {
          return true;
        }
      } catch {
        // Seguir con refresh
      }
    }

    try {
      await this.refreshTokenOnce();
      return true;
    } catch {
      // No borrar sesión aquí: un fallo de red no debe cerrar si el access sigue válido.
      return false;
    }
  }

  getUserDeviceOS(): Platforms {
    if (this.platform.is('ios')) {
      return Platforms.ios;
    } else if (this.platform.is('android')) {
      return Platforms.android;
    } else {
      return Platforms.web;
    }
  }
}
